import axios from 'axios'
import camelCase from 'lodash/fp/camelCase'
import compose from 'lodash/fp/compose'
import delay from 'lodash/delay'
import fromPairs from 'lodash/fp/fromPairs'
import get from 'lodash/get'
import hash from 'object-hash'
import keys from 'lodash/fp/keys'
import map from 'lodash/fp/map'
import reject from 'lodash/fp/reject'
import transform from 'lodash/fp/transform'
import toNumber from 'lodash/fp/toNumber'
import toPairs from 'lodash/fp/toPairs'
import * as mutations from './mutation-types'
import * as R from "ramda";

import http from '@/http'

export function _compile_path(path, context) {
    // Nach Path-Variablen suchen
    const pathVariables = path.match(/:\w+/g)

    // Wenn Path-Variablen vorhanden, diese in `path` ersetzen
    if (pathVariables !== null) {
        pathVariables.forEach(replacement => {
            // Ersetzung/Wert aus `context` lesen
            const value = context[replacement.substr(1)]

            // Variable in `path` ersetzen
            path = path.replace(replacement, value)
        })
    }

    return path
}

export function _compile_params(params, context) {
    return compose([

        // Objekt wieder zusammenbauen
        transform((result, [key, value]) => result[key] = value, {}),

        // Leere Paare ausfiltern
        reject(pair => pair.length === 0),

        // Paar[1] ggf. durch Wert aus Context ersetzen
        map(([key, value]) => {
            // Pruefen ob Wert leer ist
            if (R.isNil(value)) {
                // Leeres Paar zurueckgeben
                return []
            }

            // Wenn Wert mit `:` beginnt, dann Wert durch Wert aus `context` ersetzen
            if (value[0] === ':') {
                value = context[value.substr(1)]
            }

            return [key, value]
        }),

        // Objekt-Properties in Paare teilen
        toPairs

    ])(params)
}

export function _get_counts(headers) {
    return compose(

        fromPairs,

        // Tupel formatieren
        map(([key, count]) => [camelCase(key), toNumber(count)]),

        // Tupel aus Property-Namen und Counts erstellen
        map(key => [key.substr(8), headers[key]]),

        // Property-Namen danach filtern, ob Sie mit `x-count` beginnen
        R.filter(key => key.startsWith('x-count')),

        // Property-Namen extrahieren
        keys

    )(headers)
}

export function _request_id(path, params, payload) {
    const compiledPath = _compile_path(path, payload)
    const compiledParams = _compile_params(params, payload)
    return compiledPath + '--' + hash(compiledParams)
}

export function _create_request(method, path, params, context, data) {
    params    = R.merge(params, context.params)
    params    = _compile_params(params, context)
    const url = _compile_path(path, context)
    return http({ data, method, url, params, silent: context.silent })
}

export function _force(method, payload) {
    method = R.toUpper(method)

    // Wenn `method` schreibend ist, dann `force` auf true setzen
    if (_has_side_effects(method)) {
        return true;
    }

    return R.defaultTo(false, payload.force)
}

export function _is_writable(method) {
    return R.contains(R.toUpper(method), [ 'POST', 'PUT', 'PATCH' ])
}

export function _has_side_effects(method) {
    return R.contains(R.toUpper(method), [ 'POST', 'PUT', 'PATCH', 'DELETE' ])
}

/**
 * options:
 *  * params - URL-Params (default: {})
 *  * setCounts - Flag die bestimmt, ob `X-Count-*`-Heaeder im Store gespeichert weden sollen (default: false)
 */
export function execute({ mutation, path, responsePath, method, ...options }) {
    const params = R.defaultTo({}, options.params)
    const setCounts = R.defaultTo(false, options.setCounts)
    const skipRefresh = R.defaultTo(false, options.skipRefresh)

    const promises = {
        pendings: {},
        completes: {}
    }

    /*
     *  * body - Payload für Request (default: null)
     *  * force: Erzwingt Request / Ignoriert Cache (default: false)
     *  * model - Alias für `body`
     *  * returnType - `
     *      "body"`: Gibt nur den Body des Response zurück (default)
     *      "full"`: Gibt den kompletten Response zurück
     */
    return async ({ dispatch, commit }, payload = {}) => {
        const context = payload
        const body = R.isNil(payload.body) ? R.defaultTo(null, payload.model) : payload.body
        const force = _force(method, payload)
        const returnType = R.toLower(R.defaultTo('body', payload.returnType))
        const requestId = _request_id(path, params, payload)

        // ´promise` deklarieren
        let promise;

        // Wenn Request bereits geladen, dann `complete`-Promise nutzen
        if (force === false && promises.completes[requestId] !== undefined) {
            promise = promises.completes[requestId]
        }

        // Wenn Request noch laeuft, dann `pending`-Promise nutzen
        else if (promises.pendings[requestId] !== undefined) {
            promise = promises.pendings[requestId]
        }

        // Ansonsten Request ausfuehren bzw. Promise erstellen
        else {
            // Request ausfuehren
            promise = _create_request(method, path, params, context, body)

            // Promise als `pending` speichern
            promises.pendings[requestId] = promise
        }

        try {
            // Auf Response warten
            const response = await promise

            // Wenn Request Datensatz geschrieben hat, dann den Datensatz zusätzlich laden
            if (!skipRefresh && _is_writable(method) && R.has('id', payload)) {
                await dispatch('loadOne', { id: payload.id, force: true })
            }

            // Content aus Response lesen holen
            const content = get(response, responsePath)

            // Content in Vuex-Store setzen
            commit(mutation, { content })

            // Counts in Vuex-Store setzen
            if (setCounts === true) {
                commit(mutations.SET_COUNTS, { content: _get_counts(response.headers) })
            }

            // Promise aus `pendings` entfernen
            delete promises.pendings[requestId]

            // `complete`-Promise nach Verzögerung wieder löschen
            if (promises.completes[requestId] === undefined) {
                delay(() => delete promises.completes[requestId], 5000)
            }

            // Promise zu `completes` hinzufuegen
            promises.completes[requestId] = promise

            return returnType === 'full' ? response : content;

        } catch (e) {
            // Pending-Promise entfernen
            delete promises.pendings[requestId]

            // Exception weiterwerfen, lol
            throw e;
        }

    }
}

export function create(path, params = {}) {
    return execute({
        mutation: mutations.SET_ONE,
        responsePath: 'data',
        method: 'POST',
        params, path,
    })
}

export function update(path, options = {}) {
    return execute({
        mutation: mutations.SET_ONE,
        responsePath: 'data',
        method: 'PUT',
        path, ...options,
    })
}

export function load_one(path, params = {}) {
    return execute({
        mutation: mutations.SET_ONE,
        responsePath: 'data',
        method: 'GET',
        params, path,
    })
}

export function load_many(path, { params = {}, setCounts = false } = {}) {
    // Page-Size per Default auf 999 setzen
    params.size = params.size || 999

    return execute({
        mutation: mutations.SET_MANY,
        responsePath: 'data.content',
        method: 'GET',
        params, path, setCounts
    })
}

export function remove(path) {
    return async ({ commit }, { model }) => {
        // Auf Server loeschen
        await axios.delete(`/api/v1/${path}/${model.id}`)

        // In Store loeschen
        commit('REMOVE', { content: model })
    }
}
